import { useEffect } from 'react';
import { initCA } from '@vp/chat-anywhere';
import { Box, P, Section } from '@vp/swan';
import { useSearchParams } from 'next/navigation';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { scrollToElementById } from '@99designs/browser';
import {
    APP_LOCALE,
    BIFROST_ENABLED_LOCALES,
    useIdentityContext,
} from '@99designs/design-services-common';
import {
    VistaExperiment,
    getExperimentVariationNoExposure,
    initializeStatsig,
    isFeatureGateEnabled,
} from '@99designs/experiments';
import { PageHead } from '@99designs/feature-design-services-brief';
import {
    BrandBanner,
    DesignCategories,
    DesignConsultationBanner,
    FAQ,
    FeaturedProducts,
    FeaturedProductsType,
    Header,
    HowItWorks,
    LogoBanner,
    MoreProducts,
    RedesignHeader,
    RedesignHowItWorks,
    StickyNav,
    Support,
    SupportBanner,
    WhyHireOnVistaprint,
    getDesignProductPresenters,
    getFeaturedCareProducts,
    getProductsByKey,
    useCategoriesConfig,
} from '@99designs/feature-design-services-landing';
import { SupportedLocale, __, __url } from '@99designs/i18n';
import { PAGE_TRACKING_CONFIG, useSendPageViewOnce } from '@99designs/tracking';
import { DesignServicesAppProps } from './_app';

async function isHubPageRedesignEnabled(userId: string, locale: string) {
    if (process.env.NEXT_PUBLIC_CI_MODE === 'true') {
        return false;
    }
    await initializeStatsig();
    return isFeatureGateEnabled('expert_services_hub_page_redesign', userId, locale);
}

async function isDesignConsultationEnabled(userId: string, locale: string) {
    if (process.env.NEXT_PUBLIC_CI_MODE === 'true') {
        return false;
    }

    await initializeStatsig();
    return isFeatureGateEnabled('es__design_consultation', userId, locale);
}

async function getExperimentsForHubPage(userId: string, locale: string) {
    if (process.env.NEXT_PUBLIC_CI_MODE === 'true') {
        return [];
    }

    const wantedExperiments = ['expert_services_hub_page_keyword_search'];
    const experiments: VistaExperiment[] = [];

    await initializeStatsig();
    wantedExperiments.forEach((experiment) => {
        experiments.push({
            experimentKey: experiment,
            variationKey: getExperimentVariationNoExposure(experiment, userId, locale),
        });
    });

    return experiments;
}

interface LandingPageProps extends DesignServicesAppProps {
    featuredProducts: FeaturedProductsType;
    isHubPageRedesignEnabled: boolean;
    isDesignConsultationEnabled: boolean;
}

export const getServerSideProps: GetServerSideProps<LandingPageProps> = async (context) => {
    const { req, locale } = context;
    const siteLocale = locale || APP_LOCALE;
    //NOTE: Products are queried by fixed version on the DS landing page at present until product catalog supports translations for  all vista languages
    const nineNineProducts = await getProductsByKey(
        req,
        ['logo-essential-v3', 'gold-logo-brand-identity-pack-contest-v3'],
        siteLocale
    );

    return {
        props: {
            featuredProducts: nineNineProducts as FeaturedProductsType,
            locale: siteLocale,
            isHubPageRedesignEnabled: await isHubPageRedesignEnabled(
                req.cookies['testUserId'] || '',
                siteLocale
            ),
            isDesignConsultationEnabled: await isDesignConsultationEnabled(
                req.cookies['testUserId'] || '',
                siteLocale
            ),
            pageConfig: {
                pageTracking: {
                    ...PAGE_TRACKING_CONFIG['discover'],
                    pageName: 'Design Services:Hub Page',
                    pagePath: '/design',
                },
            },
            experiments: await getExperimentsForHubPage(
                req.cookies['testUserId'] || '',
                siteLocale
            ),
        },
    };
};

const DesignCategoriesSectionId = 'design-categories-section';
const WhyHireOnVistaprintSectionId = 'why-hire-on-vistaprint-section';
const HowItWorksSectionId = 'how-it-works-section';
const DesignConsultationSectionId = 'design-consultation-section';
const FAQSectionId = 'faq-section';
const ScrollTopMargin = 75;

export default function LandingPage({
    featuredProducts,
    isHubPageRedesignEnabled,
    isDesignConsultationEnabled,
    locale,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
    useSendPageViewOnce(locale, false);
    const searchParams = useSearchParams();

    const categories = useCategoriesConfig(locale as SupportedLocale);

    const selectedCategoryId = searchParams.get('category') ?? undefined;

    const careProducts = getFeaturedCareProducts();
    const featuredProduct = getDesignProductPresenters(locale as SupportedLocale).logoEssential;

    const { isSignedIn } = useIdentityContext();

    useEffect(() => {
        initCA();
    }, []);

    let bannerBelowHowItWorks = null;
    if (isDesignConsultationEnabled && isSignedIn) {
        bannerBelowHowItWorks = (
            <Section
                marginBottom="between-sections"
                id={DesignConsultationSectionId}
                style={{ boxShadow: 'none' }}
            >
                <DesignConsultationBanner />
            </Section>
        );
    } else if (BIFROST_ENABLED_LOCALES.includes(locale) && featuredProduct) {
        bannerBelowHowItWorks = (
            <Section marginBottom="between-sections" style={{ boxShadow: 'none' }}>
                <BrandBanner featuredProduct={featuredProduct} />
            </Section>
        );
    }

    if (isHubPageRedesignEnabled) {
        return (
            <>
                <PageHead
                    name={__('Design Services')}
                    description={__(
                        'Choose a design service that best fits your business needs. Get help designing a product or hire a freelance designer to create a new logo or brand identity.'
                    )}
                    url={__url('/design')}
                />
                <Section>
                    <RedesignHeader
                        browseDesignServicesCtaOnClick={() => {
                            scrollToElementById(DesignCategoriesSectionId, ScrollTopMargin);
                        }}
                        howItWorksCtaOnClick={() => {
                            scrollToElementById(HowItWorksSectionId, ScrollTopMargin);
                        }}
                        locale={locale as SupportedLocale}
                    />
                </Section>
                <StickyNav
                    navItems={[
                        { label: __('Design services'), href: `#${DesignCategoriesSectionId}` },
                        { label: __('Why Vistaprint?'), href: `#${WhyHireOnVistaprintSectionId}` },
                        { label: __('How it works'), href: `#${HowItWorksSectionId}` },
                        { label: __('FAQs'), href: `#${FAQSectionId}` },
                    ]}
                />
                {categories.length > 0 ? (
                    <Section
                        marginTop="between-sections"
                        marginBottom="between-sections"
                        id={DesignCategoriesSectionId}
                        style={{ boxShadow: 'none' }}
                    >
                        <DesignCategories
                            title={__('Popular design services')}
                            categories={categories}
                            locale={locale as SupportedLocale}
                            initialCategoryId={selectedCategoryId}
                        />
                    </Section>
                ) : (
                    <Section
                        marginTop="between-sections"
                        marginBottom="between-sections"
                        id={DesignCategoriesSectionId}
                        style={{ boxShadow: 'none' }}
                    >
                        <P textAlign="center">
                            Whoops - design categories could not be loaded but we are working on it.
                        </P>
                    </Section>
                )}
                <Section
                    marginBottom="between-sections"
                    id={WhyHireOnVistaprintSectionId}
                    style={{ boxShadow: 'none' }}
                >
                    <WhyHireOnVistaprint />
                </Section>
                <Section
                    marginBottom="between-sections"
                    id={HowItWorksSectionId}
                    style={{ boxShadow: 'none' }}
                >
                    <RedesignHowItWorks />
                </Section>

                {bannerBelowHowItWorks}

                <Section
                    marginBottom="between-sections"
                    id={FAQSectionId}
                    style={{ boxShadow: 'none' }}
                >
                    <FAQ />
                </Section>
                <Section style={{ boxShadow: 'none' }}>
                    <SupportBanner locale={locale} />
                </Section>
            </>
        );
    }

    let bannerBelowFeaturedProducts = null;
    if (isDesignConsultationEnabled && isSignedIn) {
        bannerBelowFeaturedProducts = (
            <Box as="section" marginTop="between-sections">
                <DesignConsultationBanner />
            </Box>
        );
    } else if (locale.includes('en')) {
        bannerBelowFeaturedProducts = <LogoBanner />;
    }

    return (
        <>
            <PageHead
                name={__('Design Services')}
                description={__(
                    'Choose a design service that best fits your business needs. Get help designing a product or hire a freelance designer to create a new logo or brand identity.'
                )}
                url={__url('/design')}
            />
            <Header />
            <HowItWorks />
            <FeaturedProducts products={{ ...featuredProducts, ...careProducts }} />

            {bannerBelowFeaturedProducts}

            <MoreProducts locale={locale} />
            <Support locale={locale} />
        </>
    );
}
